import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { NS } from '../U3204';
import { useU3204Context } from '../U3204Context';
import { u3204NormalizeMissionRef } from '../U3204Service';
import { writeLastMissionId } from '../utils/u3204LastMissionStorage';

/**
 * Deep-link `/U3204?mission=<code>` (BL-0201).
 * Priorité sur la restauration lastMission ; nettoie le query après traitement.
 */
export function useU3204MissionDeepLink(missionsInitialized: boolean): boolean {
  const { t } = useTranslation(NS);
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    accessibleMissions,
    loadingAccessibleMissions,
    setSelectedMissionId,
    setActiveMission,
    setActiveConversationId,
    setMessages,
    setStreamState,
    setActiveConversationTitle,
    setIsNewChat,
  } = useU3204Context();
  const handledRef = useRef<string | null>(null);
  const missionCode = searchParams.get('mission');

  useEffect(() => {
    if (!missionCode) {
      handledRef.current = null;
      return;
    }
    if (loadingAccessibleMissions || !missionsInitialized) return;
    if (handledRef.current === missionCode) return;
    handledRef.current = missionCode;

    const match = accessibleMissions.find((m) => m.code === missionCode);
    if (!match) {
      toast.error(t('help.missionUnavailable', { ns: NS }));
      navigate('/U3204', { replace: true });
      return;
    }

    setActiveConversationId(null);
    setMessages([]);
    setStreamState('idle');
    setActiveConversationTitle(null);
    setIsNewChat(true);
    setSelectedMissionId(match.id);
    writeLastMissionId(match.id);
    // Conserver provider/modèle de accessibleMissions (évite « Non configuré » au footer)
    setActiveMission(u3204NormalizeMissionRef(match));
    navigate('/U3204', { replace: true });
  }, [
    accessibleMissions,
    loadingAccessibleMissions,
    missionCode,
    missionsInitialized,
    navigate,
    setActiveConversationId,
    setActiveConversationTitle,
    setActiveMission,
    setIsNewChat,
    setMessages,
    setSelectedMissionId,
    setStreamState,
    t,
    toast,
  ]);

  return Boolean(missionCode);
}
